// ─── Earnings.js ───────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import API from '../utils/apiconfig';
import { useAuth } from '../context/Authcontext';
import { useGlobalError } from '../context/GlobalErrorContext';
import TablePagination from '../components/TablePagination';
import { ScrollableListBody } from '../components/ScrollableTable';
import { DEFAULT_PAGE_SIZE, parsePaginatedResponse } from '../utils/pagination';
import { formatAppDate } from '../utils/dateFormat';
import PayoutWalletNote from '../components/PayoutWalletNote';

const TXN_TYPE_LABEL = {
  referral_join: { label: 'New referral joined', color: 'text-emerald-600', bg: 'bg-emerald-50', sign: '+' },
  referral_renewal: { label: 'Referral renewed', color: 'text-blue-600', bg: 'bg-blue-50', sign: '+' },
  withdrawal: { label: 'Withdrawal', color: 'text-orange-600', bg: 'bg-orange-50', sign: '-' },
  bonus: { label: 'Admin bonus', color: 'text-violet-600', bg: 'bg-violet-50', sign: '+' },
};

export function Earnings() {
  const { user } = useAuth();
  const { reportError } = useGlobalError();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [txLoading, setTxLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) {
      navigate('/login');
      return;
    }
    api
      .get(API.EARNINGS_SUMMARY)
      .then((res) => {
        setSummary(res.data);
        setSummaryLoading(false);
      })
      .catch((err) => {
        setSummaryLoading(false);
        reportError(err);
      });
  }, [user?.id, navigate, reportError]);

  const loadTransactions = useCallback(async () => {
    setTxLoading(true);
    try {
      const res = await api.get(API.TRANSACTIONS, { params: { page, page_size: pageSize } });
      const parsed = parsePaginatedResponse(res.data, ['results', 'transactions']);
      setTransactions(parsed.items);
      setTotalPages(parsed.totalPages);
      setTotalCount(parsed.count);
      setPage(parsed.page);
    } catch (err) {
      reportError(err);
      setTransactions([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setTxLoading(false);
    }
  }, [page, pageSize, reportError]);

  useEffect(() => {
    if (!user?.id) return;
    loadTransactions();
  }, [user?.id, loadTransactions]);

  const availablePoints = summary?.available_points ?? 0;
  const pointsInr = summary?.points_inr ?? 0;
  const totalEarned = summary?.total_earned_points ?? 0;
  const thisMonthPoints = summary?.this_month_points ?? 0;

  return (
    <div className="min-h-screen bg-slate-50 py-10">
      <div className="max-w-3xl mx-auto px-6 space-y-5">
        <div className="border-b border-slate-200 pb-5 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1
              className="text-2xl font-black text-slate-900"
              style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
            >
              Earnings
            </h1>
            <p className="text-sm text-slate-400 mt-1">Your complete earnings history and point balance</p>
          </div>
          <Link
            to="/withdrawal"
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-bold text-white no-underline hover:bg-slate-800 transition-colors"
          >
            <span aria-hidden>🏦</span>
            View monthly payout
          </Link>
        </div>

        <Link
          to="/withdrawal"
          className="block rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 no-underline hover:border-emerald-300 transition-colors"
        >
          <p className="text-sm font-bold text-emerald-900">See what management will pay you</p>
          <p className="text-xs text-emerald-800/80 mt-0.5">
            Open Withdrawals for Gross amount, TDS 5%, and Net amount to your bank / UPI.
          </p>
        </Link>

        {summary?.open_payout ? (
          <PayoutWalletNote snapshot={summary.open_payout} />
        ) : null}

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
            <p
              className="text-2xl font-black text-amber-700"
              style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
            >
              ⭐ {summaryLoading ? '…' : availablePoints.toLocaleString('en-IN')}
            </p>
            <p className="text-xs text-amber-600 mt-1">Available Points</p>
            <p className="text-[10px] text-amber-400 mt-0.5">
              ≈ ₹{summaryLoading ? '…' : pointsInr.toLocaleString('en-IN')}
            </p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4 text-center">
            <p
              className="text-2xl font-black text-slate-900"
              style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
            >
              {summaryLoading ? '…' : totalEarned.toLocaleString('en-IN')}
            </p>
            <p className="text-xs text-slate-500 mt-1">Total Earned (all time)</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4 text-center col-span-2 sm:col-span-1">
            <p
              className="text-2xl font-black text-slate-900"
              style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
            >
              {summaryLoading ? '…' : thisMonthPoints.toLocaleString('en-IN')}
            </p>
            <p className="text-xs text-slate-500 mt-1">This Month</p>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Transaction History</p>
          </div>
          {txLoading && transactions.length === 0 ? null : transactions.length === 0 ? (
            <p className="text-center text-sm text-slate-500 py-12">No transactions yet.</p>
          ) : (
            <>
              <ScrollableListBody>
                {transactions.map((txn) => {
                  const t = TXN_TYPE_LABEL[txn.txn_type] || {
                    label: txn.txn_type,
                    color: 'text-slate-600',
                    bg: 'bg-slate-50',
                    sign: '',
                  };
                  return (
                    <div key={txn.id} className="flex items-center gap-3 px-5 py-4">
                      <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-xs font-semibold ${t.bg} ${t.color}`}>
                        {t.sign}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-800 truncate">{txn.description || t.label}</p>
                        <p className="text-xs text-slate-500">
                          {formatAppDate(txn.created_at)}
                        </p>
                      </div>
                      <span className={`flex-shrink-0 text-sm font-bold ${txn.points > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                        {txn.points > 0 ? '+' : ''}
                        {txn.points} pts
                      </span>
                    </div>
                  );
                })}
              </ScrollableListBody>
              <TablePagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                pageSize={pageSize}
                onPageSizeChange={(next) => { setPageSize(next); setPage(1); }}
                disabled={txLoading}
                count={totalCount}
                embedded
                className="px-4"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Earnings;
